import React from 'react';

import { usePageStore } from '@client/store/pageStore/context/pageContext';
import { calculateRelativeSpectrogramRanges } from '@client/utils/calculateRelativeSpectrogramRanges';
import i18n from '@client/utils/i18n';
import { SpectralRanges, SpectrogramPoint } from '@ledium/types';
import { AutocompleteBordered, AutocompleteOptionWithChart } from '@ledium/ui-common';
import { Alert, Grid, TextField } from '@mui/material';

import useChannelList from '../Hooks/useChannelList';
import { MixerChannel } from './SpectrumMixerChannelSettingsItem';

interface ChannelOption extends MixerChannel {
    description?: string;
    spectrogram: SpectrogramPoint[];
    spectrogramRanges: { pfd: SpectralRanges; irradiance: SpectralRanges };
}

interface SpectrumMixerLightChannelSelectProps {
    noItemsMessage?: React.ReactNode;
    filterByGroup?: boolean;
}

const SpectrumMixerLightChannelSelect = ({ noItemsMessage, filterByGroup = false }: SpectrumMixerLightChannelSelectProps) => {
    const {
        pageState: { formData },
        pageActions
    } = usePageStore();

    function getOptionLabel(option: ChannelOption) {
        return option.name;
    }

    function isOptionEqualToValue(option: ChannelOption, value: ChannelOption) {
        return option.id === value.id;
    }

    function getOptionDisabled(option: ChannelOption) {
        const linked = formData.linked as { projectId?: string } | null;
        if (option.linked && linked && option.linked.projectId !== linked.projectId) {
            if (option.linked.reason === 'UNUSED_SPECTRUM_HAS_LINKED_COLOR_CHANNEL') {
                option.linkedReason = i18n('spectrum_mix_linked_light_channel_another_mix', option.linked.spectrumMixName, option.linked.projectName) as string;
            } else {
                option.linkedReason = i18n('spectrum_mix_linked_light_channel_project', option.linked.projectName) as string;
            }
            return true;
        }
        return false;
    }

    let channelList = useChannelList() as unknown as ChannelOption[];
    if (filterByGroup && formData.groupId) {
        channelList = channelList.filter((channel) => channel.groupId === (formData.groupId as string));
    }

    function onChange(_event: React.SyntheticEvent, lightChannels: ChannelOption[]) {
        pageActions.formData.updateFormItem({
            lightChannels: lightChannels.map((channel) => ({
                ...channel,
                intensityPercent: channel.intensityPercent ?? 100,
                intensity: channel.intensity ?? 2 ** channel.resolution - 1,
                intensityRanges: calculateRelativeSpectrogramRanges(channel.spectrogramRanges, 1)
            }))
        });
    }

    function renderInput(params: object) {
        return <TextField {...(params as Record<string, unknown>)} label={i18n('select_light_channel')} variant="outlined" />;
    }

    function renderOption(props: React.HTMLAttributes<HTMLLIElement>, option: ChannelOption) {
        if (option.channel) {
            option.additionalItemListInfo = i18n('dmx_channel') + ': ' + option.channel;
        }

        return (
            <li {...props}>
                <AutocompleteOptionWithChart option={option} spectrogramData={option.spectrogram} size="small" />
            </li>
        );
    }

    if (formData.readOnly) {
        return null;
    }

    return (
        <Grid item xs={12}>
            {noItemsMessage && !formData.loading && !channelList.length && (
                <Alert sx={{ marginBottom: '8px' }} severity="warning">
                    {noItemsMessage}
                </Alert>
            )}
            <AutocompleteBordered
                multiple
                options={channelList}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={isOptionEqualToValue}
                getOptionDisabled={getOptionDisabled}
                onChange={onChange}
                value={formData.lightChannels as ChannelOption[]}
                filterSelectedOptions={true}
                renderInput={renderInput}
                renderOption={renderOption}
            />
        </Grid>
    );
};

export default React.memo(SpectrumMixerLightChannelSelect);
